import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";
import { CalendarClock, CheckCircle2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

/** Inline reminder card — appears on /inicio when the current user has days
 * of this week without any bitácora entry. Fully dismissed if all days logged. */
export function BitacoraReminderWidget() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/bitacora/reminder`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch {
        // silent
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (!data || !data.days_checked?.length) return null;

  const missingCount = data.missing_days.length;
  const daysChecked = data.days_checked.length;

  if (missingCount === 0) {
    return (
      <Card className="border-emerald-200/60 bg-emerald-50/40 dark:bg-emerald-950/10 dark:border-emerald-800/40"
            data-testid="bitacora-reminder-complete">
        <CardContent className="p-3 flex items-center gap-3 text-sm">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <span className="flex-1 text-emerald-800 dark:text-emerald-300">
            <strong>Bitácora al día</strong> · registrados todos los días de la semana.
          </span>
          <Button size="sm" variant="ghost" onClick={() => navigate("/equipo?tab=bitacora")}
                  data-testid="bitacora-reminder-cta">
            Ver <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  const dayChips = data.missing_days.map(d => {
    const dt = new Date(d + "T00:00:00Z");
    return { iso: d, label: `${WEEKDAYS[(dt.getUTCDay() + 6) % 7]} ${String(dt.getUTCDate()).padStart(2, "0")}` };
  });

  return (
    <Card className="border-amber-200/60 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-800/40"
          data-testid="bitacora-reminder-widget">
      <CardContent className="p-3 flex items-center gap-3 flex-wrap text-sm">
        <CalendarClock className="w-5 h-5 text-amber-600 flex-shrink-0" />
        <div className="flex-1 min-w-[220px]">
          <div className="font-semibold text-amber-900 dark:text-amber-200">
            Bitácora pendiente · {missingCount}/{daysChecked} día(s) sin registrar
          </div>
          <div className="text-xs text-amber-700/80 dark:text-amber-300/80 flex items-center gap-1 flex-wrap mt-1">
            {dayChips.map(d => (
              <span key={d.iso} className="inline-block px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-[10px] font-mono"
                    data-testid={`bitacora-reminder-day-${d.iso}`}>
                {d.label}
              </span>
            ))}
          </div>
        </div>
        <Button size="sm" onClick={() => navigate("/equipo?tab=bitacora")}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="bitacora-reminder-cta">
          Registrar ahora <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </CardContent>
    </Card>
  );
}
